/* eslint-disable @typescript-eslint/camelcase */

import React, { useState, useEffect } from 'react'

import { IntlProvider, useIntl, FormattedNumber, FormattedMessage } from 'react-intl'

import { ThemeProvider } from 'styled-components'
import { useDebouncedValue } from 'rooks'

import { MultiSelect, Loader, Slider, InputCurrency, theme } from '@elamajs/ui-kit'
import { Button } from '@iui-kit/components/new'

import { smartJoin } from '../CommissionCalculator/helpers'
import { SYSTEM_CODE, NO_REWARD_SYSTEM_LIST, SystemLogo } from '../CommissionCalculator/system-info'
import { messages } from '../CommissionCalculator/messages'
import { EmptySvg } from '@src/static/svg'

import {
  systemsByDefault,
  systemsByDefaultNew,
  updatedSystemKeys,
  systemsByDefaultTurnovers,
  systemsByDefaultTurnoversNew,
  order
} from './constants'

import * as Styled from './component.styled'

type Money = {
  currency: string
  amount: number
}

type CalculatorProps = {
  isNewPartner: boolean
}

const NewCalculator: React.FC<CalculatorProps> = ({ isNewPartner }) => {
  const { formatMessage } = useIntl()
  const [isYandexNetworkError, setIsYandexNetworkError] = useState(false)

  /* фикс нежелательного скролла при фокусе на инпуте в Android Chrome */

  const fixScrollInAndroidChromeEffect = () => {
    const fixScroll = () => {
      const isDesktop = window.innerWidth >= 1366

      if (isDesktop) {
        return
      }

      const { activeElement } = document

      if (!activeElement) {
        return
      }

      if (activeElement.tagName !== 'INPUT') {
        return
      }

      if (activeElement.classList.contains('tabs-switch__control')) {
        return
      }
      const timeouts = [50, 100, 150, 300, 500, 1000, 2000]

      timeouts.forEach(timeout => {
        setTimeout(() => {
          const element: any | never = activeElement

          element?.scrollIntoViewIfNeeded(true)
        }, timeout)
      })
    }

    document.addEventListener('click', fixScroll, true)

    return () => {
      document.removeEventListener('click', fixScroll, true)
    }
  }

  const scrollToForm = () => {
    const partnersSection = document.querySelector('#partners-form')
    partnersSection &&
      partnersSection.scrollIntoView({
        behavior: 'smooth'
      })
  }

  useEffect(fixScrollInAndroidChromeEffect, [])

  /* про доступные системы */

  const [availableSystemsInfo, setAvailableSystemsInfo] = useState<{
    fetching: 'idle' | 'pending' | 'success' | 'failure'
    data?: { [code: string]: string }
  }>({ fetching: 'idle' })

  const availableSystems = availableSystemsInfo.data

  const loadAvailableSystemsEffect = () => {
    setAvailableSystemsInfo({
      fetching: 'pending'
    })

    fetch('https://elama.ru/agency-api/commission/calculator/system/list')
      .then(response => response.json())
      .then(data => {
        const sortedSystems = {}
        order.forEach(key => {
          sortedSystems[key] = data[key]
        })
        const updatedData: Record<string, string> = isNewPartner
          ? { direct_common: 'Яндекс Директ', yandex_network: 'РСЯ', ...sortedSystems }
          : sortedSystems
        const updateDataValues = Object.fromEntries(
          Object.entries(updatedData).map(([key, value]) => {
            if (updatedSystemKeys[key]) {
              return [key, updatedSystemKeys[key]]
            }
            return [key, value]
          })
        )
        setAvailableSystemsInfo({
          fetching: 'success',
          data: updateDataValues
        })
      })
  }

  useEffect(loadAvailableSystemsEffect, [])

  /* про минимальный оборот */

  const [minTotalTurnoverInfo, setMinTotalTurnoverInfo] = useState<{
    fetching: 'idle' | 'pending' | 'success' | 'failure'
    data?: number
  }>({ fetching: 'idle' })

  const minTotalTurnover = minTotalTurnoverInfo.data

  const loadMinTotalTurnoverEffect = () => {
    setMinTotalTurnoverInfo({
      fetching: 'pending'
    })

    fetch('https://elama.ru/agency-api/commission/conditions')
      .then(response => response.json())
      .then(data => {
        setMinTotalTurnoverInfo({
          fetching: 'success',
          data: data.minTurnover.amount / 100
        })
      })
  }

  useEffect(loadMinTotalTurnoverEffect, [])

  /* про обороты */

  const [turnovers, setTurnovers] = useState<null | { [code: string]: number }>(null)

  const selectSystemsByDefaultEffect = () => {
    if (!availableSystems || !minTotalTurnover) {
      return
    }

    const defaultSystems = isNewPartner ? systemsByDefaultNew : systemsByDefault
    const defaultTurnovers = isNewPartner ? systemsByDefaultTurnoversNew : systemsByDefaultTurnovers
    setTurnovers(
      Object.fromEntries(
        defaultSystems
          .filter(code => {
            return code in availableSystems
          })
          .map(code => {
            return [code, defaultTurnovers[code] || 30000]
          })
      )
    )
  }

  useEffect(selectSystemsByDefaultEffect, [availableSystems, minTotalTurnover])

  /* про вознаграждение */

  const [debouncedTurnovers] = useDebouncedValue(turnovers, 500)

  const [commissionInfo, setCommissionInfo] = useState<{
    fetching: 'idle' | 'pending' | 'success' | 'failure'
    data?: {
      commission: {
        base: Money
        additional: Money
        total: Money
      }
    }
  }>({
    fetching: 'idle'
  })

  const commission = commissionInfo.data?.commission

  const loadCommissionEffect = () => {
    if (!debouncedTurnovers || !minTotalTurnover) {
      return
    }

    const totalTurnover = Object.values(debouncedTurnovers).reduce<number>(
      (sum: number, turnover: number) => sum + turnover,
      0
    )

    if (totalTurnover < minTotalTurnover) {
      return
    }

    setCommissionInfo({
      fetching: 'pending'
    })

    const body = {
      currency: 'RUB',
      turnovers: Object.entries(debouncedTurnovers).map(([code, turnover]: [string, number]) => {
        return {
          code,
          amount: turnover * 100
        }
      })
    }

    const payload = isNewPartner ? { ...body, promotionType: 'NEW_PARTNER' } : body

    fetch('https://elama.ru/agency-api/commission/calculator/calculate', {
      method: 'POST',
      body: JSON.stringify(payload)
    })
      .then(response => response.json())
      .then(data => {
        setCommissionInfo({
          fetching: 'success',
          data
        })
      })
      .catch(() => {
        setCommissionInfo({
          fetching: 'failure'
        })
      })
  }

  useEffect(loadCommissionEffect, [debouncedTurnovers, minTotalTurnover])

  /* рендеринг */

  if (!availableSystems || !minTotalTurnover) {
    return (
      <Styled.LoaderContainer>
        <Styled.Global />
        <Loader />
      </Styled.LoaderContainer>
    )
  }

  const renderModal = () => {
    if (!turnovers) {
      return null
    }

    const availableSystemCodes = Object.keys(availableSystems)

    const sortedSelectedSystemCodes = availableSystemCodes.filter(code => {
      return code in turnovers
    })

    const renderSystemSelect = () => {
      const options = availableSystemCodes.map(code => {
        return {
          label: availableSystems[code],
          caption: '',
          value: code
        }
      })

      const handleChange = (codes: string[]) => {
        let values = Object.fromEntries(
          codes.map(code => {
            const { [code]: turnover = minTotalTurnover } = turnovers

            return [code, turnover]
          })
        )
        if (values[SYSTEM_CODE.yandex_network] && !values[SYSTEM_CODE.direct_common]) {
          values = { ...values, [SYSTEM_CODE.yandex_network]: 0 }
          setIsYandexNetworkError(true)
        }
        setTurnovers(values)
      }

      return (
        <Styled.SelectSystemsContainer>
          <Styled.SelectSystemsTitle>
            <FormattedMessage id="reward.current.calculator.systems.title" />
          </Styled.SelectSystemsTitle>
          <Styled.MultiSelect>
            <MultiSelect
              options={options}
              optionsLabel={formatMessage({
                id: 'reward.current.calculator.systems.optionsLabel'
              })}
              value={sortedSelectedSystemCodes}
              onChange={handleChange}
              width={'100%' as const}
              hasAllCheckbox
              hasSearch
            />
          </Styled.MultiSelect>
        </Styled.SelectSystemsContainer>
      )
    }

    const renderTurnoverInputs = () => {
      const minSliderValue = 0

      const maxValue = 999999999

      return (
        <Styled.SystemsContainer>
          {sortedSelectedSystemCodes.map(systemCode => {
            const maxSliderValue = 10000000
            const systemName = availableSystems[systemCode]

            const value = Math.min(turnovers[systemCode], maxValue)
            let sliderValue

            if (systemCode === SYSTEM_CODE.yandex_network && isYandexNetworkError) {
              sliderValue =
                Math.max(minSliderValue, Math.min(value, maxSliderValue)) - Math.random() * 10
            } else {
              sliderValue = Math.max(minSliderValue, Math.min(value, maxSliderValue))
            }
            const handleChange = (value: number) => {
              const currentValue = isNaN(value) ? 0 : Math.min(value, maxValue)

              setTurnovers(previousTurnovers => {
                const newTurnovers = {
                  ...previousTurnovers,
                  [systemCode]: currentValue
                }

                // Обработка специальных случаев для Яндекс.Директ и РСЯ
                if (
                  systemCode === SYSTEM_CODE.yandex_network ||
                  systemCode === SYSTEM_CODE.direct_common
                ) {
                  const directValue = previousTurnovers?.[SYSTEM_CODE.direct_common] ?? 0
                  const networkValue = previousTurnovers?.[SYSTEM_CODE.yandex_network] ?? 0

                  if (systemCode === SYSTEM_CODE.yandex_network) {
                    if (currentValue > directValue) {
                      setIsYandexNetworkError(true)
                      newTurnovers[systemCode] = directValue
                    } else {
                      setIsYandexNetworkError(false)
                    }
                  } else {
                    // SYSTEM_CODE.direct_common
                    if (currentValue < networkValue) {
                      setIsYandexNetworkError(true)
                      newTurnovers[SYSTEM_CODE.yandex_network] = currentValue
                    } else {
                      setIsYandexNetworkError(false)
                    }
                  }
                }

                return newTurnovers
              })
            }

            const renderAdditionalText = () => {
              if (systemCode === SYSTEM_CODE.direct_common) {
                return (
                  <Styled.SystemSliderAdditionalText>
                    Реклама в Поиске и РСЯ
                  </Styled.SystemSliderAdditionalText>
                )
              }

              if (systemCode === SYSTEM_CODE.yandex_network) {
                return (
                  <Styled.SystemSliderAdditionalText>
                    Для расчета дополнительного вознаграждения.
                    {isYandexNetworkError && (
                      <Styled.SystemSliderAdditionalError>
                        Оборот в РСЯ не может превышать суммарный оборот в Директе.
                      </Styled.SystemSliderAdditionalError>
                    )}
                  </Styled.SystemSliderAdditionalText>
                )
              }

              if (NO_REWARD_SYSTEM_LIST.includes(systemCode)) {
                return (
                  <Styled.SystemSliderAdditionalText>
                    Влияет на&nbsp;процент вознаграждения за&nbsp;другие рекламные системы.
                    <br />
                    Вознаграждение за рекламу в&nbsp;{systemName} не&nbsp;выплачиваем.
                  </Styled.SystemSliderAdditionalText>
                )
              }

              return null
            }

            const sliderOffset =
              ![SYSTEM_CODE.direct_common, SYSTEM_CODE.yandex_network].includes(systemCode) &&
              renderAdditionalText()

            return (
              <Styled.SystemSliderContainer key={systemCode}>
                <Styled.SystemSliderInfoWrapper>
                  <Styled.SystemSliderTitle>
                    <SystemLogo systemCode={systemCode} systemName={systemName} />
                    {systemName}
                  </Styled.SystemSliderTitle>
                  <Styled.SystemSliderTextContainer>
                    {renderAdditionalText()}
                  </Styled.SystemSliderTextContainer>
                </Styled.SystemSliderInfoWrapper>
                <Styled.SystemSlider>
                  <Slider
                    min={minSliderValue}
                    max={maxSliderValue}
                    value={Math.round(sliderValue)}
                    measure="₽"
                    width={'100%' as const}
                    onChange={handleChange}
                  />
                  <Styled.SystemTurnoverWrapper>
                    <Styled.SystemTurnoverText sliderOffset={!!sliderOffset}>
                      Оборот, без НДС
                    </Styled.SystemTurnoverText>
                    <InputCurrency
                      value={String(value)}
                      currency="RUB"
                      width={120}
                      isFractional={false}
                      textAlign="left"
                      onChange={value => {
                        handleChange(Number(value))
                      }}
                    />
                  </Styled.SystemTurnoverWrapper>
                </Styled.SystemSlider>
              </Styled.SystemSliderContainer>
            )
          })}
        </Styled.SystemsContainer>
      )
    }

    const renderResult = () => {
      const currency = 'RUB'

      const selectedSystemCodes = Object.keys(turnovers)

      const onlyNoRewardSystemsSelected = selectedSystemCodes.every(systemCode => {
        return NO_REWARD_SYSTEM_LIST.includes(systemCode)
      })

      const totalTurnover = Object.values(turnovers).reduce((sum, turnover) => sum + turnover, 0)

      const totalTurnoverIsLow = totalTurnover < minTotalTurnover

      const renderReward = () => {
        const renderValue = () => {
          if (onlyNoRewardSystemsSelected) {
            return (
              <FormattedNumber
                value={0}
                currency={currency}
                style="currency"
                maximumFractionDigits={0}
              />
            )
          }

          if (totalTurnoverIsLow) {
            return (
              <FormattedNumber
                value={0}
                currency={currency}
                style="currency"
                maximumFractionDigits={0}
              />
            )
          }

          if (!commission) {
            return <Loader />
          }

          return (
            <FormattedNumber
              value={commission.total.amount / 100}
              currency={currency}
              style="currency"
              maximumFractionDigits={0}
            />
          )
        }

        return (
          <Styled.Result isNewPartner={isNewPartner}>
            <Styled.ResultItem>
              <Styled.ResultTitle>Планируемый оборот за&nbsp;месяц</Styled.ResultTitle>
              <Styled.ResultValue>
                <FormattedNumber
                  value={totalTurnover}
                  currency={currency}
                  style="currency"
                  maximumFractionDigits={0}
                />
              </Styled.ResultValue>
            </Styled.ResultItem>
            <Styled.ResultItem>
              <Styled.ResultTitle>Ваше вознаграждение</Styled.ResultTitle>
              <Styled.ResultValue>{renderValue()}</Styled.ResultValue>
            </Styled.ResultItem>
          </Styled.Result>
        )
      }

      const renderErrors = () => {
        if (onlyNoRewardSystemsSelected) {
          const names = NO_REWARD_SYSTEM_LIST.filter(systemCode => {
            return selectedSystemCodes.includes(systemCode)
          }).map(systemCode => {
            return availableSystems[systemCode]
          })

          return (
            <Styled.ResultError>
              За {smartJoin(names)} вознаграждение не выплачиваем
            </Styled.ResultError>
          )
        }

        if (totalTurnoverIsLow) {
          return (
            <Styled.ResultError>
              Рассчитаем вознаграждение при обороте от{' '}
              <FormattedNumber
                value={minTotalTurnover}
                currency={currency}
                style="currency"
                maximumFractionDigits={0}
              />
            </Styled.ResultError>
          )
        }

        if (commissionInfo.fetching === 'failure') {
          return (
            <Styled.ResultError>
              Произошла ошибка, попробуйте указать другие значения
            </Styled.ResultError>
          )
        }

        return null
      }

      if (!selectedSystemCodes.length) {
        return (
          <Styled.ResultContainer>
            <Styled.EmptyResult>
              <EmptySvg />
              <Styled.EmptyResultTitle>Вознаграждение не&nbsp;расcчитано</Styled.EmptyResultTitle>
              <Styled.EmptyResultText>
                Для расчёта вознаграждения выберите хотя бы одну рекламную систему
              </Styled.EmptyResultText>
            </Styled.EmptyResult>
          </Styled.ResultContainer>
        )
      }

      return (
        <Styled.ResultContainer isNewPartner={isNewPartner}>
          {renderReward()}
          {renderErrors()}
          <Styled.ResultInfo>Калькулятор не учитывает бонусы и купоны</Styled.ResultInfo>
          <Styled.ResultInfoDisclamer>
            Предварительный расчет, для более точного расчета ознакомьтесь с&nbsp;ограничениями,
            предусмотренными{' '}
            <Styled.ResultInfoLink
              target="_blank"
              href="https://elama.ru/legal_docs/usloviya-partnerskoy-programmy/partners-program_smb.pdf"
              rel="noreferrer"
            >
              Партнерской программой для партнеров и&nbsp;фрилансеров
            </Styled.ResultInfoLink>
            . Не&nbsp;является публичной офертой.
          </Styled.ResultInfoDisclamer>
          {isNewPartner && (
            <Styled.PartnerLink onClick={scrollToForm} as={Button} variant={Button.Variant.BLACK}>
              Стать партнером
            </Styled.PartnerLink>
          )}
        </Styled.ResultContainer>
      )
    }

    return (
      <Styled.Container>
        <Styled.ContentLayout>
          <Styled.ContentWrapper>
            <Styled.Content isNewPartner={isNewPartner}>
              {renderSystemSelect()}
              {renderTurnoverInputs()}
            </Styled.Content>
          </Styled.ContentWrapper>
          <Styled.Preview>{renderResult()}</Styled.Preview>
        </Styled.ContentLayout>
      </Styled.Container>
    )
  }

  return (
    <>
      <Styled.Global />
      {renderModal()}
    </>
  )
}

const withProviders = (Component: React.ComponentType): React.FC<CalculatorProps> => {
  const NewComponent: React.ComponentType = props => {
    return (
      <ThemeProvider theme={theme}>
        <IntlProvider locale="ru" messages={messages}>
          <Component {...props} />
        </IntlProvider>
      </ThemeProvider>
    )
  }

  return NewComponent
}

export default withProviders(NewCalculator)
